'use client';

import { Prefix } from '@gnsx/genesys.js';
import * as ENGINE from '@gnsx/genesys.js';
import { AssetPath, type Constructor, resolvePath } from '@gnsx/genesys.js';
import { BUILT_PROJECT_FOLDER, DEFAULT_GAME_BUNDLE_NAME, PROJECT_PATH_PREFIX } from '@gnsx/genesys.js';


import { ICON_NAME } from './iconConstants';
import { displayCase } from './nameHelpers';

import type { ClassInfo, IClassInfo, SnapshotIn } from '@/editor/store/models';

import { buildProject } from '@/editor/core/actions/editorActions';
import { store } from '@/editor/store';

const DEBUG_LOG = { logClassInfos: false };

export type ClassPickerType = 'actor' | 'component';

const CLASS_TYPES = {
  included: [ENGINE.Actor, ENGINE.SceneComponent] as const,
  excluded: [ENGINE.PlayerController] as const
} as const;

// TODO: probably better for this to be static class data.
const CLASS_CATEGORIES = [
  [ENGINE.Pawn, 'Pawn'],
  [ENGINE.Actor, 'Actor'],
  [ENGINE.LightComponent, 'Lights'],
  [ENGINE.SoundComponent, 'Audio'],
  [ENGINE.SceneComponent, ''],
] as const;

const getClassName = (constructor: any): string => {
  return ENGINE.ClassRegistry['reverseRegistry'].get(constructor) ?? '';
};

const getClassCategory = (constructor: Constructor, isActor: boolean): string => {
  if (!isActor) {
    const name = getClassName(constructor).toLowerCase();
    if (name.includes('mesh')) return 'Mesh';
    if (name.includes('movement')) return 'Movement';
  }
  return CLASS_CATEGORIES.find(([type]) => isMatchingClass(constructor, type))?.[1] ?? '';
};

// Helper function to check if constructor matches or inherits from type
const isMatchingClass = (constructor: Constructor, classToCheck: any | any[]): boolean => {
  if (Array.isArray(classToCheck)) {
    return classToCheck.some(cls => isMatchingClass(constructor, cls));
  }
  return constructor === classToCheck || constructor.prototype instanceof classToCheck;
};

export const CLASS_PREFIXES = [Prefix.ENGINE, Prefix.GAME] as const;

/** Shared helper to get filtered class names from registry */
export function getFilteredClassNames() {
  const classRegistry = ENGINE.ClassRegistry.getRegistry();
  const classPrefixesDisplay = CLASS_PREFIXES.map(prefix => displayCase(prefix));
  const classNames = Array.from(classRegistry.keys()).filter(key => CLASS_PREFIXES.some(prefix => key.startsWith(prefix)));
  return { classRegistry, classNames, classPrefixesDisplay };
}

/** Build ClassInfo from className */
export function buildClassInfo(className: string, classPrefixesDisplay: string[], category: string): SnapshotIn<typeof ClassInfo> {
  const prefixIndex = CLASS_PREFIXES.findIndex(prefix => className.startsWith(prefix));
  const type = prefixIndex >= 0 ? classPrefixesDisplay[prefixIndex] : '';
  const displayName = displayCase(className.replace(CLASS_PREFIXES[prefixIndex] ?? '', ''));
  const constructor = ENGINE.ClassRegistry.getRegistry().get(className);
  const description = constructor ? ENGINE.getClassMetadata(constructor)?.description : undefined;
  return { className, displayName, category, type, description };
}

/** Gets class information for all registered classes. */
export async function getClassInfos(shouldRegisterGameClasses = true): Promise<Record<string, SnapshotIn<typeof ClassInfo>[]>> {
  if (shouldRegisterGameClasses) {
    try {
      await registerGameClasses();
    } catch (error) {
      console.error('Error registering game classes', error);
    }
  }

  const { classRegistry, classNames, classPrefixesDisplay } = getFilteredClassNames();

  const infosMap = Object.fromEntries(CLASS_TYPES.included.map(type => [getClassName(type), []])) as Record<string, SnapshotIn<typeof ClassInfo>[]>;

  classNames.forEach(className => {
    const classConstructor = classRegistry.get(className);
    if (!classConstructor) return;
    if (isMatchingClass(classConstructor, CLASS_TYPES.excluded)) return;
    if (ENGINE.ClassRegistry.isAbstractClass(classConstructor)) return;

    const matchingType = CLASS_TYPES.included.find(type => isMatchingClass(classConstructor, type));
    if (matchingType) {
      const category = getClassCategory(classConstructor, matchingType === ENGINE.Actor);
      infosMap[getClassName(matchingType)].push(buildClassInfo(className, classPrefixesDisplay, category));
    }
  });

  if (DEBUG_LOG.logClassInfos) console.log('infosMap AFTER', infosMap);
  return infosMap;
}

/** Gets default options for a given class name. */
export function getClassDefaultOptions(className: string): Record<string, any> {
  const classConstructor = ENGINE.ClassRegistry.getClass(className);
  if (!classConstructor) return {};
  return (classConstructor as any).DEFAULT_OPTIONS;
}

/** Gets ClassInfo for a given class name from the assets store. */
export function getClassInfo(className: string): IClassInfo | undefined {
  const { actorClassMap, componentClassMap } = store.assets;
  return actorClassMap.get(className) ?? componentClassMap.get(className);
}

export async function registerGameClasses(): Promise<void> {
  const bundlePath = AssetPath.fromString(AssetPath.join(PROJECT_PATH_PREFIX, BUILT_PROJECT_FOLDER, DEFAULT_GAME_BUNDLE_NAME));
  const storageProvider = store.project.makeScopedStorageProvider();
  try {
    const bundleExists = await storageProvider.exists(bundlePath);
    if (!bundleExists) {
      const result = await buildProject();

      if (!result) {
        throw new Error('Failed to build project before registering game classes.');
      }
    }
    const bundleText = await storageProvider.downloadFileAsText(bundlePath);
    // Pass ENGINE explicitly so createInjectedDependencies snapshots every export into a
    // stable plain object before eval. This prevents Turbopack lazy-binding TDZ errors when
    // game class bodies read ENGINE.* (extends, decorators, computed keys) during evaluation.
    const injectedDependencies = ENGINE.createInjectedDependencies(ENGINE);

    ENGINE.ClassRegistry.clearGameClasses();
    // Create a module-like object to simulate CommonJS environment
    const moduleObj = { exports: {} };
    const run = new Function('require', 'module', bundleText);
    run(injectedDependencies, moduleObj);

    // Apply any exports to the global scope if needed
    if (moduleObj.exports && typeof moduleObj.exports === 'object') {
      Object.assign(window, moduleObj.exports);
    }

    // Note: Property metadata is auto-registered when game.js bundle loads above.
    // The editor can now query ENGINE.getPropertyMetadata(target) directly.
  } catch (error) {
    console.error('Error registering game classes', error);
  }
}

export const RESERVED_CLASS_NAMES: { [key: string]: true } = Object.freeze({
  Abstract: true,
  Any: true,
  Async: true,
  Boolean: true,
  Class: true,
  Constructor: true,
  Declare: true,
  Default: true,
  Enum: true,
  Export: true,
  Extends: true,
  False: true,
  Function: true,
  Implements: true,
  Import: true,
  Interface: true,
  Let: true,
  Module: true,
  Namespace: true,
  Never: true,
  New: true,
  Null: true,
  Number: true,
  Object: true,
  Package: true,
  Private: true,
  Protected: true,
  Public: true,
  Readonly: true,
  Require: true,
  Set: true,
  Static: true,
  String: true,
  Super: true,
  Symbol: true,
  This: true,
  True: true,
  Type: true,
  Undefined: true,
  Var: true,
  Void: true,
  Yield: true
});

export function isValidClassNameCharacter(name: string): boolean {
  if (!/^[A-Z][A-Za-z0-9_]*$/.test(name)) {
    return false;
  }
  return true;
}

export function checkValidNewClassName(name: string): { success: boolean, reason: string } {
  if (name.length === 0) {
    return { success: false, reason: 'Name cannot be empty.' };
  }
  if (!isValidClassNameCharacter(name)) {
    return { success: false, reason: 'Name must start with a capital letter and contain only letters or numbers.' };
  }
  if (RESERVED_CLASS_NAMES[name]) {
    return { success: false, reason: 'Cannot use reserved word.' };
  }
  const { actorClasses, componentClasses } = store.assets;
  if (name.toLowerCase() === 'component' || actorClasses.some(cls => cls.shortClassName.toLowerCase() === name.toLowerCase()) || componentClasses.some(cls => cls.shortClassName.toLowerCase() === name.toLowerCase())) {
    return { success: false, reason: `Class ${name} already exists.` };
  }
  return { success: true, reason: '' };
}

export function isSubclass(child: Function | null | undefined, parent: Function): boolean {
  if (typeof child !== 'function' || typeof parent !== 'function') return false;
  let proto: any = child;
  while (proto) {
    if (proto === parent) return true;
    proto = Object.getPrototypeOf(proto);
  }
  return false;
}

// Re-export ICON_NAME for backward compatibility
export { ICON_NAME } from './iconConstants';

/** Get icon path for a given actor, component, scene component object, or class name */
export async function getClassIconPath(objectOrClassName: ENGINE.Actor | ENGINE.SceneComponent | string, excludedIconNames?: string[]): Promise<string | undefined> {
  let iconName: string | null;

  if (typeof objectOrClassName === 'string') {
    // Handle class name string
    const classInfo = getClassInfo(objectOrClassName);
    iconName = classInfo?.classIcon ?? null;
  } else {
    // Handle object instance
    const object = objectOrClassName;
    iconName = object.getEditorClassIcon();

    if (object instanceof ENGINE.Actor && iconName === ICON_NAME.Actor) {
      // Check scene components first
      const sceneComponents = object.getComponents(ENGINE.SceneComponent);
      for (const component of sceneComponents) {
        const componentIcon = component.getEditorClassIcon();
        if (componentIcon && componentIcon !== ICON_NAME.SceneComponent) {
          iconName = componentIcon;
          break;
        }
      }
    }
  }

  if (!iconName) return undefined;
  if (excludedIconNames?.includes(iconName)) return undefined;

  return await resolveClassIconPath(iconName);
};

export async function resolveClassIconPath(nameOrPath: string): Promise<string | undefined> {
  // Check if iconName contains a dot (indicating it's already a path)
  if (nameOrPath.includes('.')) {
    const assetPath = AssetPath.fromString(nameOrPath);
    if (assetPath.isResolved()) {
      return assetPath.getResolvedPath();
    }
    const path = await resolvePath(assetPath);
    return path.getResolvedPath();
  }
  // If just a name, prefix with .icons/objects/ and postfix with .png
  return `/.icons/objects/${nameOrPath}.png`;
}